import { useContext, useMemo, useState } from "react";
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { LuArrowRight, LuPalette, LuPlus, LuSearch } from "react-icons/lu";
import DeleteItemModal from "../components/DeleteItemModal";
import SaveMenuButton from "../components/SaveMenuButton";
import CategoryDialog from "../components/menu-editor/CategoryDialog";
import IconPickerDialog from "../components/menu-editor/IconPickerDialog";
import ItemDialog from "../components/menu-editor/ItemDialog";
import MenuPreviewDialog from "../components/menu-editor/MenuPreviewDialog";
import SortableCategoryCard from "../components/menu-editor/SortableCategoryCard";
import ThemePickerDialog from "../components/menu-editor/ThemePickerDialog";
import { categoryTemplates } from "../config/menuCategoryTemplates";
import { getMenuThemeMeta } from "../config/menuThemes";
import { UserContext } from "../context/UserContext";
import useMenuEditor, { entityKey } from "../hooks/useMenuEditor";
import { getMenuOverviewPath } from "../services/menuOverviewModel";

const MenuEditor = () => {
  const { menuId = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const editor = useMenuEditor({ menuId, initialMenu: location.state });
  const { state, dispatch, loading, saving, isDirty } = editor;
  const [query, setQuery] = useState("");
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [itemDialog, setItemDialog] = useState(null);
  const [appearanceCategoryKey, setAppearanceCategoryKey] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);
  const themeMeta = getMenuThemeMeta(state.theme);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const template = categoryTemplates[user?.activityType];
  const suggestions = (template?.categories || [])
    .map((category) => category.name)
    .filter((name) => !state.categories.some((category) => category.name === name));
  const appearanceCategory = state.categories.find((category) => entityKey(category) === appearanceCategoryKey);
  const normalizedQuery = query.trim().toLocaleLowerCase("fa-IR");
  const visibleCategories = useMemo(() => state.categories.map((category) => ({
    ...category,
    filteredItems: !normalizedQuery
      ? category.items
      : category.items.filter((item) => [item.name, item.description]
        .some((value) => String(value || "").toLocaleLowerCase("fa-IR").includes(normalizedQuery))),
  })).filter((category) => !normalizedQuery || category.name.toLocaleLowerCase("fa-IR").includes(normalizedQuery) || category.filteredItems.length), [normalizedQuery, state.categories]);

  const guardNavigation = (destination) => {
    if (!isDirty || window.confirm("تغییرات ذخیره‌نشده دارید. بدون ذخیره خارج می‌شوید؟")) navigate(destination);
  };

  const handleSave = async () => {
    try {
      const savedMenu = await editor.save();
      toast.success("منو با موفقیت ذخیره شد.");
      navigate(getMenuOverviewPath(savedMenu?._id || menuId));
    } catch (error) {
      toast.error(error.message || "ذخیره منو ناموفق بود.");
    }
  };

  const categoryDragEnd = ({ active, over }) => {
    if (over && active.id !== over.id) dispatch({ type: "MOVE_CATEGORY", activeKey: active.id, overKey: over.id });
  };

  if (loading) {
    return <div className="mx-auto max-w-6xl animate-pulse space-y-4 p-4" aria-label="در حال بارگذاری منو"><div className="h-12 rounded-xl bg-gray-100" /><div className="h-48 rounded-2xl bg-gray-100" /><div className="h-48 rounded-2xl bg-gray-100" /></div>;
  }

  return (
    <div className="flex min-h-full flex-col bg-gray-50/60" dir="rtl">
      <main className="mx-auto w-full max-w-6xl flex-1 space-y-5 p-4 pb-8 sm:p-6">
        <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <button type="button" onClick={() => guardNavigation(getMenuOverviewPath(menuId))} className="mb-2 inline-flex min-h-10 items-center gap-2 rounded-lg text-sm font-medium text-gray-600 hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/20"><LuArrowRight /> بازگشت به مدیریت منو</button>
            <h1 className="text-2xl font-black text-gray-900">{menuId ? "ویرایش منو" : "ساخت منوی جدید"}</h1>
            {template?.label && <p className="mt-1 text-sm text-gray-500">پیشنهادهای دسته‌بندی برای {template.label} در دسترس است.</p>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => setThemeDialogOpen(true)} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-bold text-gray-700 hover:border-primary/40"><LuPalette /> ظاهر منو: {themeMeta.label}</button>
            <button type="button" onClick={() => setCategoryDialogOpen(true)} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-secondary px-4 text-sm font-bold text-white hover:bg-secondary/90"><LuPlus /> افزودن دسته‌بندی</button>
          </div>
        </header>

        <section className="grid gap-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:grid-cols-[minmax(0,1fr)_minmax(240px,0.45fr)]">
          <label className="text-sm font-medium text-gray-700">عنوان منو
            <input value={state.title} onChange={(event) => dispatch({ type: "SET_TITLE", title: event.target.value })} placeholder="مثلاً منوی اصلی رستوران" className="mt-2 w-full rounded-xl border border-gray-200 p-3 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
          </label>
          <label className="text-sm font-medium text-gray-700">جست‌وجوی آیتم
            <span className="relative mt-2 block"><LuSearch className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="نام آیتم یا توضیحات" className="w-full rounded-xl border border-gray-200 py-3 pl-3 pr-10 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" /></span>
          </label>
        </section>

        {state.error && <div role="alert" className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">{state.error}</div>}

        {state.categories.length ? (
          visibleCategories.length ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis]} onDragEnd={categoryDragEnd}>
              <SortableContext items={visibleCategories.map(entityKey)} strategy={verticalListSortingStrategy}>
                <div className="space-y-4">
                  {visibleCategories.map((category) => {
                    const categoryKey = entityKey(category);
                    return (
                      <SortableCategoryCard
                        key={categoryKey}
                        category={category}
                        filteredItems={category.filteredItems}
                        onRename={(name) => dispatch({ type: "RENAME_CATEGORY", categoryKey, name })}
                        onAppearance={() => setAppearanceCategoryKey(categoryKey)}
                        onAddItem={() => setItemDialog({ categoryKey, item: null })}
                        onEditItem={(item) => setItemDialog({ categoryKey, item })}
                        onDuplicateItem={(itemKey) => editor.duplicateItem(categoryKey, itemKey)}
                        onDeleteItem={(item) => setDeleteTarget({ categoryKey, item })}
                        onDeleteCategory={() => {
                          if (window.confirm(`دسته «${category.name}» و همه آیتم‌های آن حذف شود؟`)) dispatch({ type: "DELETE_CATEGORY", categoryKey });
                        }}
                        onMoveItem={(activeKey, overKey) => dispatch({ type: "MOVE_ITEM", categoryKey, activeKey, overKey })}
                      />
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          ) : <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-12 text-center text-sm text-gray-500">نتیجه‌ای برای جست‌وجوی شما پیدا نشد.</div>
        ) : (
          <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-12 text-center"><p className="font-bold text-gray-800">هنوز دسته‌بندی‌ای ندارید.</p><p className="mt-1 text-sm text-gray-500">برای شروع، اولین دسته منو را اضافه کنید.</p><button type="button" onClick={() => setCategoryDialogOpen(true)} className="mt-4 min-h-11 rounded-xl bg-primary px-5 text-sm font-bold text-white">افزودن دسته‌بندی</button></div>
        )}
      </main>

      <SaveMenuButton onSave={handleSave} onPreview={() => setPreviewOpen(true)} saving={saving} isDirty={isDirty} lastSavedAt={state.lastSavedAt} />
      <CategoryDialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} suggestions={suggestions} onSubmit={editor.addCategory} />
      <ItemDialog open={Boolean(itemDialog)} onClose={() => setItemDialog(null)} item={itemDialog?.item} onSubmit={(item) => editor.upsertItem(itemDialog.categoryKey, item, itemDialog.item ? entityKey(itemDialog.item) : undefined)} />
      <IconPickerDialog open={Boolean(appearanceCategory)} onClose={() => setAppearanceCategoryKey(null)} icon={appearanceCategory?.icon} iconColor={appearanceCategory?.iconColor || "#F88225"} onChange={({ icon, iconColor }) => dispatch({ type: "SET_CATEGORY_APPEARANCE", categoryKey: appearanceCategoryKey, icon, iconColor })} />
      <DeleteItemModal open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} itemName={deleteTarget?.item?.name} onConfirm={() => dispatch({ type: "DELETE_ITEM", categoryKey: deleteTarget.categoryKey, itemKey: entityKey(deleteTarget.item) })} />
      <ThemePickerDialog open={themeDialogOpen} onClose={() => setThemeDialogOpen(false)} theme={state.theme} onChange={(theme) => dispatch({ type: "SET_THEME", theme })} />
      <MenuPreviewDialog open={previewOpen} onClose={() => setPreviewOpen(false)} title={state.title} categories={state.categories} />
    </div>
  );
};

export default MenuEditor;
